/**
 * Normalizes a file extension to include a leading dot and be lowercase.
 *
 * @param extension The raw extension string
 * @returns Normalized extension (e.g., ".pdf")
 */
export function normalizeExtension(extension: string): string {
  const trimmed = (extension || '').trim()
  if (!trimmed) return ''
  return trimmed.startsWith('.') ? trimmed.toLowerCase() : `.${trimmed.toLowerCase()}`
}

/**
 * Detects the MIME type based on a file extension.
 *
 * @param extension The file extension (with or without dot)
 * @returns The MIME type string
 */
export function getMimeTypeFromExtension(extension: string): string {
  const ext = normalizeExtension(extension)
  switch (ext) {
    case '.pdf':
      return 'application/pdf'
    case '.png':
      return 'image/png'
    case '.jpg':
    case '.jpeg':
      return 'image/jpeg'
    case '.gif':
      return 'image/gif'
    case '.webp':
      return 'image/webp'
    case '.txt':
      return 'text/plain'
    case '.json':
      return 'application/json'
    case '.xml':
      return 'application/xml'
    case '.zip':
      return 'application/zip'
    default:
      return 'application/octet-stream'
  }
}

/**
 * Pretty-prints an XML string by inserting line breaks and indentation between tags,
 * so it can be rendered in a readable, read-only preview.
 *
 * @param xml The raw XML content
 * @returns The formatted (indented) XML string
 */
export function formatXml(xml: string): string {
  const trimmed = (xml || '').trim()
  if (!trimmed) return ''

  const withBreaks = trimmed.replace(/>\s*</g, '>\n<')
  const lines = withBreaks.split('\n')
  const INDENT = '  '
  let pad = 0
  const formattedLines = lines.map((line) => {
    const node = line.trim()
    if (!node) return ''

    if (/^<\/[^>]+>/.test(node)) {
      pad = Math.max(pad - 1, 0)
    }

    const indented = INDENT.repeat(pad) + node

    const isSelfClosing = /\/>\s*$/.test(node)
    const isDeclarationOrComment = /^<\?/.test(node) || /^<!--/.test(node) || /^<!\[CDATA\[/.test(node)
    const isClosingTag = /^<\/[^>]+>/.test(node)
    const hasInlineClosingTag = /^<[^>]+>.*<\/[^>]+>$/.test(node)

    if (!isSelfClosing && !isDeclarationOrComment && !isClosingTag && !hasInlineClosingTag) {
      pad += 1
    }

    return indented
  })

  return formattedLines.filter((line) => line !== '').join('\n')
}
